/**
 * Floating AI Trainer panel — bottom-right persistent panel that hosts
 * the Claude-backed chat (spec §2).
 *
 * - Zone 1: header (always visible) with minimize button
 * - Zone 2: scrollable conversation
 * - Zone 3: quick-suggestion chips (contextual)
 * - Zone 4: input + send
 *
 * Auto-minimizes when any modal/overlay pushes the overlay counter
 * (see `useTraining().effectiveMinimized`).
 *
 * API: POST /api/training/chat — provider abstracted behind
 *      TRAINING_AI_PROVIDER (claude by default).
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { chat as chatApi } from "@/lib/trainingApi";
import { useTraining } from "@/lib/trainingContext";
import { Minus, Send, X, Sparkles, Plus } from "lucide-react";

const WELCOME_MSG = "Hi! I'm your AI trainer. Ask me anything about the CRM, or pick a topic from the picker to start a guided walkthrough.";

function QuickSuggestion({ text, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(text)}
      data-testid={`training-quick-${text.slice(0, 18).replace(/\s+/g, "-").toLowerCase()}`}
      style={{
        border: "0.5px solid #B5D4F4",
        borderRadius: 12,
        padding: "3px 10px",
        fontSize: 10,
        color: "#185FA5",
        background: "#f0f7ff",
        whiteSpace: "nowrap",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {text}
    </button>
  );
}

function getContextualSuggestions(path, topic) {
  if (topic) {
    return [
      `Walk me through ${topic.title}`,
      "What's the next step?",
      "Why does this matter?",
    ];
  }
  if (path.startsWith("/leads")) return ["How do I import leads?", "Filter by stage", "Add a new lead"];
  if (path.startsWith("/lead-focus")) return ["Send an email", "Schedule a demo", "Update the stage"];
  if (path.startsWith("/communications")) return ["Create a follow-up task", "Use AI batch email", "Send an SMS"];
  if (path.startsWith("/dialer")) return ["Start calling", "Use the script", "Post-call form"];
  if (path.startsWith("/quotes")) return ["Build a quote", "Submit for approval", "Generate contract"];
  if (path.startsWith("/demo-bookings")) return ["Schedule a demo", "Log an outcome", "View the team schedule"];
  return ["Navigate the CRM", "What can my role do?", "Show me around"];
}

export default function TrainingFloatingPanel() {
  const { enabled, activeTopic, sessionId, effectiveMinimized, setPanelMinimized, panelMinimized } = useTraining();
  const { pathname } = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Reset greeting when training turned on fresh
  useEffect(() => {
    if (enabled && messages.length === 0) {
      setMessages([{ role: "assistant", text: WELCOME_MSG }]);
    }
    if (!enabled) {
      setMessages([]);
      setInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, effectiveMinimized]);

  if (!enabled) return null;

  const minimized = effectiveMinimized;

  const sendMessage = async (text) => {
    const payload = (text || "").trim();
    if (!payload) return;
    setMessages((m) => [...m, { role: "user", text: payload }]);
    setInput("");
    setSending(true);
    try {
      const res = await chatApi({
        message: payload,
        session_id: sessionId,
        topic_slug: activeTopic?.slug || null,
        current_page: pathname,
      });
      setMessages((m) => [...m, {
        role: "assistant",
        text: res.reply || "(no reply)",
        provider: res.provider,
        model: res.model,
      }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const reason = (detail && typeof detail === "object" && detail.reason) || (typeof detail === "string" ? detail : null);
      setMessages((m) => [...m, {
        role: "assistant",
        isError: true,
        text: reason
          ? `I can't reach the AI trainer right now (${reason}). Please try again in a moment, or open the in-app help.`
          : "I can't reach the AI trainer right now. Please try again in a moment.",
      }]);
    } finally {
      setSending(false);
    }
  };

  const suggestions = getContextualSuggestions(pathname, activeTopic);

  return (
    <div
      data-testid="training-floating-panel"
      data-minimized={minimized ? "true" : "false"}
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: 300,
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
        border: "0.5px solid #ddd",
        zIndex: 190,
        display: "flex",
        flexDirection: "column",
        maxHeight: minimized ? 52 : 440,
        overflow: "hidden",
        transition: "max-height 0.2s ease",
      }}
    >
      {/* Zone 1 — Header */}
      <div
        style={{
          background: "#0F2942",
          borderRadius: "14px 14px 0 0",
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: minimized ? "pointer" : "default",
        }}
        onClick={() => { if (minimized && !effectiveMinimized === false) { /* noop */ } if (minimized && panelMinimized) setPanelMinimized(false); }}
      >
        <div
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "#2563eb",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Sparkles style={{ width: 14, height: 14 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>Enable AI Trainer</div>
          <div style={{ color: "#8BA5C0", fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {activeTopic
              ? `Guiding: ${activeTopic.title}`
              : "Ask me anything about the CRM"}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPanelMinimized((v) => !v); }}
          data-testid="training-panel-minimize"
          aria-label={panelMinimized ? "Expand trainer" : "Minimize trainer"}
          style={{ background: "transparent", border: "none", color: "#8BA5C0", cursor: "pointer", padding: 2 }}
        >
          {panelMinimized ? <Plus style={{ width: 16, height: 16 }} /> : <Minus style={{ width: 16, height: 16 }} />}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); window.__trainingExit?.(); }}
          data-testid="training-panel-close"
          aria-label="Exit training"
          style={{ background: "transparent", border: "none", color: "#8BA5C0", cursor: "pointer", padding: 2 }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>

      {/* Zone 2 — conversation (hidden when minimized) */}
      {!minimized && (
        <div
          ref={scrollRef}
          data-testid="training-conversation"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "10px 12px",
            background: "#fff",
            minHeight: 120,
            maxHeight: 220,
          }}
        >
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={m.role === "user" ? "training-msg-user" : "training-msg-assistant"}
              style={{
                background: m.role === "user" ? "#2563eb" : (m.isError ? "#FDE2E2" : "#f0f7ff"),
                color: m.role === "user" ? "#fff" : (m.isError ? "#9A1C1C" : "#111"),
                borderRadius: m.role === "user" ? "8px 0 8px 8px" : "0 8px 8px 8px",
                padding: "8px 10px",
                fontSize: 12,
                lineHeight: 1.6,
                maxWidth: m.role === "user" ? "80%" : "90%",
                marginLeft: m.role === "user" ? "auto" : 0,
                marginBottom: 8,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {m.text}
            </div>
          ))}
          {sending && (
            <div data-testid="training-sending-indicator" style={{ fontSize: 11, color: "#888", padding: "4px 2px" }}>
              AI trainer is thinking…
            </div>
          )}
        </div>
      )}

      {/* Zone 3 — quick suggestions */}
      {!minimized && (
        <div
          data-testid="training-quick-suggestions"
          style={{
            padding: "6px 10px",
            display: "flex",
            gap: 6,
            overflowX: "auto",
            borderTop: "0.5px solid #eee",
            scrollbarWidth: "none",
          }}
        >
          {suggestions.map((s) => (
            <QuickSuggestion key={s} text={s} onClick={sendMessage} />
          ))}
        </div>
      )}

      {/* Zone 4 — input */}
      {!minimized && (
        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
          style={{
            borderTop: "0.5px solid #eee",
            padding: "8px 10px",
            display: "flex",
            gap: 6,
            alignItems: "center",
          }}
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything about the CRM…"
            data-testid="training-panel-input"
            disabled={sending}
            style={{
              flex: 1,
              border: "0.5px solid #ddd",
              borderRadius: 8,
              padding: "7px 10px",
              fontSize: 12,
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            data-testid="training-panel-send"
            style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "7px 10px",
              fontSize: 12,
              fontWeight: 500,
              cursor: sending || !input.trim() ? "not-allowed" : "pointer",
              opacity: sending || !input.trim() ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Send style={{ width: 12, height: 12 }} />
          </button>
        </form>
      )}
    </div>
  );
}
